import { useState, useEffect, useRef } from "react";
import {
  Bell,
  Check,
  CheckCheck,
  LogOut,
  PanelLeftClose,
  Bookmark,
  Trash2,
  X,
  ArrowRight,
  AtSign,
} from "lucide-react";
import { useUser } from "../contexts/UserContext";
import { useNotification } from "../contexts/NotificationContext";
import { useBookmark } from "../contexts/BookmarkContext";
import AdvancedSearch from "./AdvancedSearch";
import { useProject } from "../contexts/ProjectContext";
import Avatar from "./Avatar";
import { useNavigate } from "react-router-dom";
import CardModal from "./CardModal";
import AppLogo from "./AppLogo";

const timeAgo = (dateString) => {
  const now = new Date();
  const past = new Date(dateString);
  const diff = Math.floor((now - past) / 1000); // seconds

  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;

  const days = Math.floor(diff / 86400);

  // Show "X day ago" up to 30 days
  if (days <= 30) {
    return `${days} day ago`;
  }

  // After 30 days → show date with year
  return past.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (name) => {
  if (!name) return "S";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getNotificationSenderDetails = (n) => {
  if (n.sender && n.sender.name) {
    return {
      name: n.sender.name,
      avatar: n.sender.avatar || getInitials(n.sender.name),
      color: n.sender.color || "#6366f1",
    };
  }

  // Try to parse sender name from notification message (e.g., "system moved...")
  if (n.message) {
    const match = n.message.match(
      /^([A-Za-z0-9_\s\-]+?)\s+(mentioned|replied|assigned|unassigned|added|removed|created|updated|granted|revoked|changed|moved)/i
    );
    if (match && match[1]) {
      const extractedName = match[1].trim();
      return {
        name: extractedName,
        avatar: getInitials(extractedName),
        color: "#6366f1",
      };
    }
  }

  return {
    name: "System",
    avatar: "SY",
    color: "#6366f1",
  };
};

const isMentionNotification = (n) =>
  n.type === "comment_mention" ||
  n.type === "mention" ||
  Boolean(n.title && /mention/i.test(n.title)) ||
  Boolean(n.message && /mention/i.test(n.message));

const parseNotificationContent = (n) => {
  const msg = n.message || "";
  const cardTitle = n.relatedCard?.title || "";

  // 1. Status move: "... moved "<card>" from <StatusA> to <StatusB>"
  const moveMatch = msg.match(/moved\s+"([^"]+)"\s+from\s+(.+?)\s+to\s+(.+)$/i);
  if (moveMatch) {
    return {
      type: "move",
      cardName: moveMatch[1],
      action: "moved status on",
      fromStatus: moveMatch[2],
      toStatus: moveMatch[3],
    };
  }

  // 2. Mention: "... mentioned you in a comment on "<card>""
  const mentionMatch = msg.match(/mentioned you in a comment on\s+"([^"]+)"/i);
  if (mentionMatch) {
    return {
      type: "mention",
      cardName: mentionMatch[1],
      action: "mentioned you on",
    };
  }

  // 3. Reply: "... replied to your comment on "<card>""
  const replyMatch = msg.match(/replied to your comment on\s+"([^"]+)"/i);
  if (replyMatch) {
    return {
      type: "reply",
      cardName: replyMatch[1],
      action: "replied to comment on",
    };
  }

  // 4. Assigned: "You have been assigned to the card "<card>""
  const assignMatch = msg.match(/assigned to the card\s+"([^"]+)"/i);
  if (assignMatch) {
    return {
      type: "assigned",
      cardName: assignMatch[1],
      action: "assigned you to",
    };
  }

  // 5. Unassigned: "You have been removed from the card "<card>""
  const unassignMatch = msg.match(/removed from the card\s+"([^"]+)"/i);
  if (unassignMatch) {
    return {
      type: "unassigned",
      cardName: unassignMatch[1],
      action: "removed you from",
    };
  }

  return {
    type: "general",
    cardName: cardTitle || (msg.match(/"([^"]+)"/) ? msg.match(/"([^"]+)"/)[1] : null),
    action: n.title || "Card Update",
    rawMessage: msg,
  };
};

const getNotificationTypeConfig = (type, title = "", message = "") => {
  if (
    type === "comment_mention" ||
    type === "mention" ||
    /mention/i.test(title) ||
    /mention/i.test(message)
  ) {
    return {
      badgeBg: "bg-purple-600",
      badgeIcon: "at",
    };
  }
  if (type === "card_assigned" || /assign/i.test(title)) {
    return {
      badgeBg: "bg-emerald-600",
      badgeIcon: "user",
    };
  }
  if (type === "card_unassigned") {
    return {
      badgeBg: "bg-rose-600",
      badgeIcon: "user-minus",
    };
  }
  if (type === "card_updated" || /status|move/i.test(title) || /moved/i.test(message)) {
    return {
      badgeBg: "bg-amber-500",
      badgeIcon: "move",
    };
  }
  return {
    badgeBg: "bg-blue-600",
    badgeIcon: "bell",
  };
};

const getNotificationRedirectUri = (n) => {
  const projId = n.relatedProject?._id || n.relatedProject;
  const cardId = n.relatedCard?._id || n.relatedCard;
  if (!projId) return null;
  if (["credential_access_revoked", "project_activity"].includes(n.type)) {
    return `/project/${projId}/edit`;
  }
  if (cardId) {
    return `/project/${projId}/card/${cardId}`;
  }
  return `/project/${projId}`;
};

const Header = ({ onMenuClick, onToggleSidebar, sidebarCollapsed }) => {
  const { user, logout } = useUser();
  const bellRef = useRef(null);
  const bookmarkRef = useRef(null);
  const userMenuRef = useRef(null);
  const [activeTab, setActiveTab] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    totalCount,
    unreadCount: contextUnreadCount,
    fetchAllNotifications,
    hasLoadedAll,
    isLoadingAll,
  } = useNotification();
  const { bookmarkedCards, removeBookmark } = useBookmark();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [open, setOpen] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [selectedBookmarkCard, setSelectedBookmarkCard] = useState(null);
  const unreadCount =
    typeof contextUnreadCount === "number"
      ? contextUnreadCount
      : notifications.filter((n) => !n.read).length;
  const unreadTabCount = notifications.filter(
    (n) => !n.read && !isMentionNotification(n)
  ).length;
  const memberUnreadCount = notifications.filter(
    (n) => !n.read && isMentionNotification(n)
  ).length;
  const { projects, loading, fetchProjects } = useProject();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate("/");
  };
  const sortedNotifications = [...notifications].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  const filteredNotifications = sortedNotifications.filter((n) => {
    if (activeTab === "unread") {
      return !n.read && !isMentionNotification(n);
    }
    if (activeTab === "member") {
      return isMentionNotification(n);
    }
    return true;
  });

  const displayNotifications = showAll
    ? filteredNotifications
    : filteredNotifications.slice(0, 4);

  const totalForCurrentTab =
    activeTab === "unread"
      ? Math.max(unreadCount || 0, filteredNotifications.length)
      : activeTab === "all"
      ? Math.max(totalCount || 0, filteredNotifications.length)
      : filteredNotifications.length;

  const handleToggleViewAll = async () => {
    if (!showAll) {
      setShowAll(true);
      if (!hasLoadedAll && fetchAllNotifications) {
        await fetchAllNotifications();
      }
    } else {
      setShowAll(false);
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setOpen(false);
        setShowAll(false);
      }
      if (bookmarkRef.current && !bookmarkRef.current.contains(event.target)) {
        setShowBookmarks(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="h-12 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-3 transition-colors duration-200">
      <div className="h-full flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onToggleSidebar}
              className="hidden lg:flex items-center justify-center w-8 h-8 shrink-0 rounded-lg text-gray-700 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-700 dark:hover:text-white transition-colors duration-200"
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <PanelLeftClose className="w-4 h-4"/>
            </button>
            <AppLogo className="h-7 w-auto" />
          </div>

          {/* Mobile Search */}
          <div className="md:hidden">
            <AdvancedSearch />
          </div>

          {/* <div className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">
              Project Management
            </h1>
          </div> */}
        </div>

        <div className="flex items-center space-x-4">
          {/* Advanced Search */}
          <div className="hidden md:block">
            <AdvancedSearch />
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Bookmarks */}
          <div className="relative" ref={bookmarkRef}>
            <button
              onClick={() => setShowBookmarks(!showBookmarks)}
              className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 relative transition-colors duration-200"
              title="Bookmarked Cards"
            >
              <Bookmark className={`w-4 h-4 ${bookmarkedCards.length > 0 ? "text-amber-500 fill-amber-500" : "text-gray-700 dark:text-slate-400"}`} />
              {bookmarkedCards.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center shadow-sm">
                  {bookmarkedCards.length}
                </span>
              )}
            </button>

            {/* Bookmarks Dropdown */}
            {showBookmarks && (
              <div className="absolute right-0 top-12 mt-2 w-[320px] sm:w-[380px] bg-white dark:bg-slate-800 shadow-2xl border border-gray-200 dark:border-slate-700 rounded-xl p-3 z-[100] animate-slideDownFade">
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-100 dark:border-slate-700/60">
                  <div className="flex items-center gap-2">
                    <Bookmark className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <h3 className="text-[12px] font-semibold text-gray-800 dark:text-white">
                      Bookmarked Cards
                    </h3>
                    <span className="px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[11px] font-semibold">
                      {bookmarkedCards.length}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowBookmarks(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {bookmarkedCards.length === 0 ? (
                  <div className="py-8 px-4 text-center">
                    <Bookmark className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-slate-600" />
                    <p className="text-[12px] font-medium text-gray-600 dark:text-slate-400">
                      No bookmarked cards yet
                    </p>
                    <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">
                      Click the bookmark icon on any card to save it here for quick access.
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
                    {bookmarkedCards.map((c) => {
                      const cardId = c._id || c.id;
                      const projId = c.project?._id || c.project;
                      const projName = c.project?.name || "Project";
                      const cardDisplayId = `#${c.cardNumber || cardId?.slice(-4) || "0000"}`;

                      return (
                        <div
                          key={cardId}
                          onClick={() => {
                            if (projId) {
                              navigate(`/project/${projId}/card/${cardId}`);
                            } else {
                              setSelectedBookmarkCard(c);
                            }
                            setShowBookmarks(false);
                          }}
                          className="group relative flex items-start justify-between p-2.5 rounded-lg border border-gray-100 dark:border-slate-700/60 bg-gray-50/50 dark:bg-slate-900/50 hover:bg-blue-50/60 dark:hover:bg-slate-700/60 transition-all cursor-pointer"
                        >
                          <div className="min-w-0 flex-1 pr-2">
                            <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 dark:text-slate-400 mb-0.5">
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (projId) {
                                    navigate(`/project/${projId}`);
                                    setShowBookmarks(false);
                                  }
                                }}
                                className="truncate text-blue-600 dark:text-blue-400 font-semibold hover:underline cursor-pointer"
                                title={`Go to project: ${projName}`}
                              >
                                {projName}
                              </span>
                              <span className="font-semibold text-gray-500 dark:text-slate-400">
                                • {cardDisplayId}
                              </span>
                            </div>
                            <h4 className="text-[12px] font-semibold text-gray-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 line-clamp-2 transition-colors">
                              {c.title}
                            </h4>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeBookmark(cardId);
                              }}
                              className="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                              title="Remove bookmark"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="relative" ref={bellRef}>
            {/* Bell Icon */}
            <button
              onClick={() => setOpen(!open)}
              className={`h-8 w-8 flex items-center justify-center rounded-lg relative transition-colors duration-200 focus:outline-none ${
                open
                  ? "bg-blue-50 text-blue-600 dark:bg-slate-700 dark:text-white"
                  : "hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-400"
              }`}
              title="Notifications"
            >
              <Bell className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center ring-2 ring-white dark:ring-slate-800 shadow-sm animate-pulse">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {/* Dropdown */}
            {open && (
              <div className="absolute -right-12 top-12 mt-2 w-[420px] max-w-[92vw] bg-white dark:bg-slate-800 shadow-2xl border border-gray-200 dark:border-slate-700 rounded-2xl p-4 z-[100] opacity-0 translate-y-[-10px] animate-slideDownFade">
                {/* Header */}
                <div className="flex items-center justify-between mb-3 px-0.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                      <Bell className="w-3.5 h-3.5" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <span className="px-2 py-0.5 text-[11px] font-bold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                        {unreadCount} new
                      </span>
                    )}
                  </div>

                  {/* Mark all as read */}
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-all focus:outline-none"
                    >
                      <Check className="w-3 h-3 stroke-[2.5]" />
                      <span>Mark all read</span>
                    </button>
                  )}
                </div>

                {/* Tabs */}
                <div className="flex items-center p-1 bg-gray-100/90 dark:bg-slate-700/50 rounded-xl mb-3 border border-gray-200/40 dark:border-slate-600/30">
                  {[
                    { id: "all", label: "All", badge: null },
                    {
                      id: "unread",
                      label: "Unread",
                      badge: unreadTabCount > 0 ? unreadTabCount : null,
                    },
                    {
                      id: "member",
                      label: "Member",
                      badge: memberUnreadCount > 0 ? memberUnreadCount : null,
                    },
                  ].map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.id);
                          setShowAll(false);
                        }}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 text-xs font-semibold rounded-lg transition-all focus:outline-none select-none ${
                          isActive
                            ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm"
                            : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                      >
                        <span>{tab.label}</span>
                        {tab.badge !== null && (
                          <span
                            className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                              isActive
                                ? "bg-blue-50 text-blue-600 dark:bg-blue-900/60 dark:text-blue-300"
                                : "bg-gray-200/80 text-gray-600 dark:bg-slate-600 dark:text-slate-300"
                            }`}
                          >
                            {tab.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Notifications list */}
                {filteredNotifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                    {activeTab === "unread" ? (
                      <>
                        <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-2.5 shadow-inner">
                          <CheckCheck className="w-6 h-6" />
                        </div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">
                          All caught up!
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          You have no unread notifications.
                        </p>
                      </>
                    ) : activeTab === "member" ? (
                      <>
                        <div className="w-12 h-12 rounded-full bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 mb-2.5 shadow-inner">
                          <AtSign className="w-6 h-6" />
                        </div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">
                          No member mentions
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          When someone mentions you in a comment, it will appear here.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-400 dark:text-slate-400 mb-2.5 shadow-inner">
                          <Bell className="w-6 h-6" />
                        </div>
                        <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">
                          No notifications yet
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          You're all up to date with project activity.
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="relative group max-h-[62vh] overflow-y-auto space-y-2.5 pr-1">
                      {displayNotifications.map((n) => {
                        const senderDetails = getNotificationSenderDetails(n);
                        const parsed = parseNotificationContent(n);
                        const typeConfig = getNotificationTypeConfig(n.type, n.title, n.message);
                        const redirectUri = getNotificationRedirectUri(n);
                        const projectName = n.relatedProject?.name;

                        return (
                          <div
                            key={n._id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!n.read) markAsRead(n._id);
                              if (redirectUri) {
                                setOpen(false);
                                navigate(redirectUri);
                              }
                            }}
                            className={`group relative rounded-xl p-3 border transition-all duration-200 cursor-pointer ${
                              !n.read
                                ? "bg-gradient-to-r from-blue-50/80 via-indigo-50/30 to-white dark:from-blue-950/40 dark:via-slate-800 dark:to-slate-800 border-blue-200/90 dark:border-blue-800/60 shadow-sm"
                                : "bg-white dark:bg-slate-800/90 hover:bg-gray-50/90 dark:hover:bg-slate-750 border-gray-150 dark:border-slate-700/60 hover:border-gray-300 dark:hover:border-slate-600"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Avatar with type badge */}
                              <div className="relative shrink-0 mt-0.5">
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-bold shadow-sm"
                                  style={{ backgroundColor: senderDetails.color }}
                                >
                                  {senderDetails.avatar}
                                </div>
                                <span
                                  className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shadow-sm text-white ring-2 ring-white dark:ring-slate-800 ${typeConfig.badgeBg}`}
                                >
                                  {typeConfig.badgeIcon === "at" ? (
                                    "@"
                                  ) : typeConfig.badgeIcon === "user" ? (
                                    <Check className="w-2.5 h-2.5 stroke-[3]" />
                                  ) : typeConfig.badgeIcon === "move" ? (
                                    <ArrowRight className="w-2.5 h-2.5 stroke-[3]" />
                                  ) : (
                                    <Bell className="w-2.5 h-2.5" />
                                  )}
                                </span>
                              </div>

                              {/* Content */}
                              <div className="min-w-0 flex-1">
                                {/* Header row: Sender name, action, time ago, unread dot */}
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="min-w-0 flex items-center gap-1.5 truncate">
                                    <span className="text-xs font-bold text-gray-900 dark:text-white truncate">
                                      {senderDetails.name}
                                    </span>
                                    <span className="text-[11px] text-gray-500 dark:text-slate-400 truncate font-normal">
                                      {parsed.action}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <span className="text-[10px] text-gray-400 dark:text-slate-500 whitespace-nowrap">
                                      {timeAgo(n.createdAt)}
                                    </span>
                                    {!n.read && (
                                      <span
                                        className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400 shadow-sm"
                                        title="Unread"
                                      />
                                    )}
                                  </div>
                                </div>

                                {/* Card Title */}
                                {parsed.cardName && (
                                  <p className="text-xs font-semibold text-gray-800 dark:text-slate-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1 mb-1">
                                    "{parsed.cardName}"
                                  </p>
                                )}

                                {/* Status Change flow badges */}
                                {parsed.type === "move" && parsed.fromStatus && parsed.toStatus && (
                                  <div className="flex items-center gap-1.5 mb-1.5 text-[11px]">
                                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 font-medium">
                                      {parsed.fromStatus}
                                    </span>
                                    <ArrowRight className="w-3 h-3 text-gray-400 dark:text-slate-500 shrink-0" />
                                    <span className="px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-300 font-semibold border border-blue-200/50 dark:border-blue-800/50">
                                      {parsed.toStatus}
                                    </span>
                                  </div>
                                )}

                                {/* Fallback message if no cardName */}
                                {!parsed.cardName && parsed.rawMessage && (
                                  <p className="text-xs text-gray-600 dark:text-slate-400 line-clamp-2 mb-1.5">
                                    {parsed.rawMessage}
                                  </p>
                                )}

                                {/* Footer row: Project pill & View Card link */}
                                <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 dark:border-slate-700/60 mt-1">
                                  {projectName ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-700/60 px-1.5 py-0.5 rounded">
                                      📁 {projectName}
                                    </span>
                                  ) : (
                                    <span />
                                  )}

                                  {redirectUri && (
                                    <span className="text-[11px] text-blue-600 dark:text-blue-400 font-semibold group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-0.5">
                                      View Card <ArrowRight className="w-3 h-3" />
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* View All / Less */}
                    {(filteredNotifications.length > 4 || (!hasLoadedAll && totalForCurrentTab > 4)) && (
                      <div className="mt-3 text-center border-t border-gray-100 dark:border-slate-700/60 pt-2.5">
                        <button
                          onClick={handleToggleViewAll}
                          disabled={isLoadingAll}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-all disabled:opacity-60"
                        >
                          {isLoadingAll
                            ? "Loading..."
                            : showAll
                            ? "View less ↑"
                            : `View all (${totalForCurrentTab}) ↓`}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* User Profile */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              title={user?.name}
              className="h-8 flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors duration-200"
            >
              <Avatar user={user} size="sm" />
            </button>

            {/* User Dropdown Menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 py-2 z-50">
                <div className="px-4 py-2 border-b border-gray-100 dark:border-slate-700">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bookmarked Card Modal */}
      {selectedBookmarkCard && (
        <CardModal
          card={selectedBookmarkCard}
          onClose={() => setSelectedBookmarkCard(null)}
          onCardUpdated={() => {
            fetchProjects && fetchProjects();
          }}
        />
      )}
    </header>
  );
};

export default Header;
